import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, ErrorView, Loading, Page, Section, Sheet, useFail, useToast } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { dateShort, daysUntil, label, money, time, when } from "../lib/format.ts";
import { useMe } from "../lib/session.tsx";
import { TaskSheet } from "./Person.tsx";

const STATES = [["current", "On the market"], ["sold", "Sold"], ["withdrawn", "Withdrawn"], ["all", "All"]] as const;

/** A listing's main photo from Rex (400px), or a plain placeholder when Rex has none. */
export function ListingThumb({ photo }: { photo?: { thumb?: string | null; url?: string } | null }) {
  const src = photo?.thumb ?? photo?.url;
  return src ? <img className="thumb" src={src} alt="" loading="lazy" decoding="async" />
    : <div className="thumb ph" aria-hidden="true"><Icon name="home" /></div>;
}

export function Listings() {
  const [state, setState] = useState<string>("current");
  const [mine, setMine] = useState(false);
  const q = useQuery({ queryKey: ["listings", state, mine], queryFn: () => api.get<any[]>(`/listings?state=${state}${mine ? "&mine=1" : ""}`) });
  return (
    <Page title="Listings">
      <div className="chips">
        {STATES.map(([k, l]) => <button key={k} className={"chip" + (state === k ? " on" : "")} onClick={() => setState(k)}>{l}</button>)}
        <button className={"chip" + (mine ? " on" : "")} onClick={() => setMine(!mine)}>I work on</button>
      </div>
      <div className="banner"><Icon name="lock" size={18} /><span>A read-only copy of Rex. Rex stays the master for listings and portals; the CRM adds who works each one.</span></div>
      {q.isLoading ? <Loading /> : q.error ? <ErrorView error={q.error} /> : q.data!.length === 0 ? <Empty>No listings.</Empty> : (
        <div className="list" style={{ marginTop: 12 }}>
          {q.data!.map((l) => {
            const exp = l.authority_expires ? daysUntil(l.authority_expires) : null;
            return (
              <Link key={l.id} to={`/listings/${l.id}`} className="row">
                <ListingThumb photo={l.photo} />
                <div className="main-col">
                  <div className="title">{l.address_display}</div>
                  <div className="sub">{l.price_advertise_as} · {l.beds} bed · {l.interested} interested{l.agents.some((a: any) => a.role === "conjunction") ? ` · with ${l.agents.find((a: any) => a.role === "conjunction").name}` : ""}</div>
                </div>
                <div className="side">
                  {l.under_contract ? <span className="badge solid">Under contract</span> : l.state === "sold" ? <span className="badge white">Sold</span> : l.state === "current" ? <span className="badge blue">On market</span> : <span className="badge">{l.state}</span>}
                  {exp != null && l.state === "current" && exp <= 14 && <div className={exp <= 7 ? "danger" : ""} style={{ marginTop: 4, fontSize: 12 }}>Form 6 {exp < 0 ? "expired" : `ends in ${exp}d`}</div>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Page>
  );
}

export function Listing() {
  const { id } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const [sheet, setSheet] = useState<null | "agents" | "task">(null);
  const q = useQuery({ queryKey: ["listing", id], queryFn: () => api.get(`/listings/${id}`) });
  if (q.isLoading) return <Page title="Listing" back><Loading /></Page>;
  if (q.error || !q.data) return <Page title="Listing" back><ErrorView error={q.error} /></Page>;
  const l = q.data;
  const statuses = ["under_contract", "offered", "inspected", "enquired", "purchased", "not_interested"];
  return (
    <Page title={l.address_display} back quiet>
      {l.images?.length > 0 && (
        <div className="photos" aria-label={`${l.images.length} photos from Rex`}>
          {l.images.map((p: any, i: number) => (
            <a key={p.url} href={p.large ?? p.url} target="_blank" rel="noreferrer">
              <img src={p.thumb ?? p.url} alt={i === 0 ? l.address_display : ""} loading={i < 3 ? "eager" : "lazy"} decoding="async" />
            </a>))}
        </div>
      )}
      <div className="record-head"><div className="avatar blue"><Icon name="listing" /></div>
        <div style={{ minWidth: 0 }}>
          <h2>{l.headline}</h2>
          <div className="meta">{l.address_display}</div>
          <div className="badges" style={{ marginTop: 8 }}>
            {l.under_contract ? <span className="badge solid">Under contract</span> : <span className="badge blue">{l.state === "current" ? "On market" : l.state}</span>}
            <span className="badge">{l.price_advertise_as}</span>
            <span className="badge">Rex {l.rex_listing_id}</span>
          </div>
        </div>
      </div>
      <div className="banner"><Icon name="lock" size={18} /><span>Copy of Rex, read-only{l.sync?.last_ok_at ? `, last synced ${when(l.sync.last_ok_at)}` : ""}. Change the listing in Rex; it flows here within 15 minutes.</span></div>

      <dl className="kv">
        <dt>Home</dt><dd>{[l.beds && `${l.beds} bed`, l.baths && `${l.baths} bath`, l.cars && `${l.cars} car`, l.land_area_m2 && `${Number(l.land_area_m2).toLocaleString()} m²`].filter(Boolean).join(" · ")}</dd>
        <dt>Authority</dt><dd>{label(l.authority_type) || l.authority_type} · {dateShort(l.authority_start)} to {dateShort(l.authority_expires)}</dd>
        {l.sold_date && <><dt>Sold</dt><dd>{dateShort(l.sold_date)} for {money(l.sold_price_cents)}</dd></>}
        <dt>Vendors</dt><dd>{l.vendors.length ? l.vendors.map((v: any, i: number) => <span key={v.person_id}>{i > 0 && ", "}<Link className="blue" to={`/people/${v.person_id}`}>{v.display_name}</Link></span>) : <span className="muted">Not visible to you</span>}</dd>
        <dt>Agents</dt><dd>{l.agents.map((a: any) => `${a.name} (${a.role})`).join(", ")}{me.user.role !== "agent" && <> · <button className="blue" style={{ background: "none", border: 0, padding: 0 }} onClick={() => setSheet("agents")}>Change</button></>}</dd>
        {l.open_times?.length > 0 && <><dt>Open homes</dt><dd>{l.open_times.map((t: any) => `${dateShort(t.start.slice(0, 10))} ${time(t.start)}`).join(", ")}</dd></>}
      </dl>
      <div className="actions pad"><button className="btn ghost sm" onClick={() => setSheet("task")}><Icon name="plus" />Task</button></div>

      <Section title="Interested buyers" count={l.buyers.length}>
        {l.buyers.length === 0 ? <Empty>No enquiries yet.</Empty> : statuses.map((s) => {
          const group = l.buyers.filter((b: any) => b.status === s);
          return group.length ? (
            <div key={s} style={{ marginBottom: 10 }}>
              <div className="pad dim" style={{ fontSize: 12, fontWeight: 700, margin: "6px 0" }}>{label(s).toUpperCase()} · {group.length}</div>
              <div className="list">{group.map((b: any) => (
                <Link key={b.id} to={`/people/${b.person_id}`} className="row"><div className="main-col"><div className="title">{b.display_name}</div>
                  <div className="sub">{[b.interest_level && `${label(b.interest_level)} interest`, b.stage && `Deal: ${b.stage}`, b.notes].filter(Boolean).join(" · ")}</div></div>
                  <span className="when">{when(b.first_enquiry_at)}</span></Link>
              ))}</div>
            </div>
          ) : null;
        })}
      </Section>

      <Section title="REA and Domain stats">
        {l.stats.length === 0 ? <Empty>Stats arrive from the 08:00 portal job once the server copy is running.</Empty> : (
          <div className="scroll-x pad"><table className="table"><thead><tr><th>Portal</th><th>Week to</th><th>Views</th></tr></thead><tbody>
            {l.stats.map((s: any) => <tr key={s.source + s.period_end}><td>{s.source}</td><td>{dateShort(s.period_end)}</td><td className="num">{s.metrics?.views ?? "—"}</td></tr>)}
          </tbody></table></div>
        )}
      </Section>

      <Section title="Notes and feedback" count={l.notes.length}>
        {l.notes.length === 0 ? <Empty>None.</Empty> : <div className="timeline">{l.notes.map((n: any) => (
          <div key={n.id} className="tl"><div className="ic"><Icon name="note" /></div><div><div className="hd"><b style={{ color: "var(--ink)" }}>{label(n.kind) || n.kind.replace(/_/g, " ")}</b><span>{when(n.occurred_at)}</span><span>{n.author}</span></div><div className="bd">{n.body}</div></div></div>))}</div>}
      </Section>

      <div className="pad dim" style={{ fontSize: 13, margin: "16px 0" }}>{l.description}</div>
      {sheet === "agents" && <AgentsSheet listing={l} onClose={() => setSheet(null)} onDone={() => qc.invalidateQueries({ queryKey: ["listing", id] })} />}
      {sheet === "task" && <TaskSheet listingId={l.id} onClose={() => setSheet(null)} onDone={() => {}} />}
    </Page>
  );
}

function AgentsSheet({ listing, onClose, onDone }: { listing: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const fail = useFail();
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.get<any[]>("/users") });
  const [roles, setRoles] = useState<Record<string, string>>(Object.fromEntries(listing.agents.map((a: any) => [a.user_id, a.role])));
  const save = async () => {
    try {
      await api.put(`/listings/${listing.id}/agents`, { agents: Object.entries(roles).filter(([, r]) => r).map(([user_id, role]) => ({ user_id, role })) });
      toast("Agents saved"); onDone(); onClose();
    } catch (e) { fail(e); }
  };
  return (
    <Sheet title="Who works this listing" onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn" onClick={save}>Save</button></>}>
      {(users.data ?? []).map((u) => (
        <div key={u.id} className="row" style={{ padding: "8px 0" }}>
          <div className="main-col"><div className="title">{u.name}</div><div className="sub">{label(u.role)}</div></div>
          <select className="select" style={{ width: 150 }} value={roles[u.id] ?? ""} onChange={(e) => setRoles({ ...roles, [u.id]: e.target.value })}>
            <option value="">Not on it</option><option value="lead">Lead</option><option value="second">Second</option><option value="conjunction">Conjunction</option>
          </select>
        </div>
      ))}
      <div className="muted" style={{ fontSize: 13 }}>A conjunction agent sees this listing, its buyers and its messages, and nothing else of yours.</div>
    </Sheet>
  );
}
